import React, { useEffect, useState } from "react";
import {
  Loader2, CheckCircle2, XCircle, ShieldCheck, Users as UsersIcon, Trash2,
} from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  adminListUsers,
  adminApproveUser,
  adminRejectUser,
  adminUpdateUserRole,
  adminDeleteUser,
  adminListUserCompanies,
  adminSetUserCompany,
} from "@/lib/api";

const NO_COMPANY = "__none__";

const STATUS_TABS = [
  { id: "pending", label: "Pendientes" },
  { id: "approved", label: "Aprobados" },
  { id: "rejected", label: "Rechazados" },
  { id: "__all__", label: "Todos" },
];

const ROLES = [
  { id: "cliente", label: "Cliente" },
  { id: "consultor", label: "Consultor" },
  { id: "admin", label: "Administrador" },
];

function StatusBadge({ status }) {
  const map = {
    pending: ["bg-amber-100 border-amber-300 text-amber-800", "Pendiente"],
    approved: ["bg-emerald-100 border-emerald-300 text-emerald-800", "Aprobado"],
    rejected: ["bg-red-100 border-red-300 text-red-700", "Rechazado"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 border-slate-300 text-slate-700", status];
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${cls}`}>
      {label}
    </span>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  // Company link (only used to scope the `cliente` role's Dashboard / Métricas)
  const [companies, setCompanies] = useState([]);
  const [companyOf, setCompanyOf] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const filters = tab === "__all__" ? {} : { status: tab };
      const [d, c] = await Promise.all([
        adminListUsers(filters),
        adminListUserCompanies().catch(() => ({ companies: [], assignments: [] })),
      ]);
      setUsers(d.users || []);
      setCompanies(c.companies || []);
      setCompanyOf(Object.fromEntries((c.assignments || []).map((a) => [a.id, a.company_slug])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando usuarios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const updateCompany = async (u, slug) => {
    try {
      const r = await adminSetUserCompany(u.id, slug === NO_COMPANY ? null : slug);
      setCompanyOf((m) => ({ ...m, [u.id]: r.company_slug || undefined }));
      toast.success(r.company_slug ? `Empresa asignada: ${r.company || r.company_slug}` : "Empresa quitada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al asignar la empresa.");
    }
  };

  const approve = async (u) => {
    try {
      await adminApproveUser(u.id);
      toast.success(`Aprobado: ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar.");
    }
  };

  const reject = async (u) => {
    if (!window.confirm(`Rechazar la cuenta de ${u.email}?`)) return;
    try {
      await adminRejectUser(u.id);
      toast.success(`Rechazado: ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rechazar.");
    }
  };

  const updateRole = async (u, role) => {
    // Fix 7 (N13): confirm (the server refuses that an admin demotes itself)
    if (role === u.role || !window.confirm(`¿Cambiar el rol de ${u.email} de «${u.role}» a «${role}»?`)) return;
    try {
      await adminUpdateUserRole(u.id, role);
      toast.success(`Rol actualizado: ${u.email} → ${role}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar rol.");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Eliminar definitivamente la cuenta ${u.email}?`)) return;
    try {
      await adminDeleteUser(u.id);
      toast.success("Eliminado.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar.");
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink">
      <TopNav />
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <p className="eyebrow flex items-center gap-1.5">
          <UsersIcon className="h-3 w-3" /> Administración · Usuarios
        </p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">
          Aprobación y gestión de cuentas
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Los clientes que se registran quedan en estado{" "}
          <strong>pendiente</strong> hasta que un administrador los apruebe.
          Aquí también puede promover usuarios al rol <strong>consultor</strong>{" "}
          o <strong>administrador</strong>.
        </p>

        <div className="mt-8 flex items-center gap-2 border-b border-slate-200">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`relative px-4 py-2 text-[12px] font-medium transition ${
                tab === t.id ? "text-ink" : "text-slate-500 hover:text-ink"
              }`}
            >
              {t.label}
              {tab === t.id && (
                <span className="pointer-events-none absolute inset-x-2 -bottom-px h-[2px] bg-gradient-to-r from-accent-blue to-accent-purple" />
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : users.length === 0 ? (
          <div className="mt-10 rounded-sm border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500"
               data-testid="users-empty">
            No hay cuentas en este estado.
          </div>
        ) : (
          <div className="mt-8 divide-y divide-slate-200 border border-slate-200">
            {users.map((u) => (
              <article key={u.id} className="grid grid-cols-1 gap-3 p-5 lg:grid-cols-[1fr_auto] lg:items-center"
                       data-testid={`user-row-${u.id}`}>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-lg text-ink">
                      {u.name || u.email}
                    </p>
                    <StatusBadge status={u.status} />
                  </div>
                  <p className="mt-0.5 text-[12px] text-slate-500">
                    <span className="font-mono text-accent-blue">{u.email}</span>
                    {u.company ? <> · {u.company}</> : null}
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">
                    creado: {new Date(u.created_at).toLocaleString("es-CO")}{" "}
                    {u.approved_at ? `· aprobado: ${new Date(u.approved_at).toLocaleString("es-CO")}` : ""}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  {u.role === "cliente" && (
                    <Select value={companyOf[u.id] || NO_COMPANY} onValueChange={(v) => updateCompany(u, v)}>
                      <SelectTrigger className="h-8 w-44 rounded-sm border-slate-300 text-[11px]"
                                     data-testid={`company-select-${u.id}`}>
                        <SelectValue placeholder="Empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_COMPANY}>Sin empresa</SelectItem>
                        {companies.map((c) => (
                          <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                    <SelectTrigger className="h-8 w-36 rounded-sm border-slate-300 text-[11px]"
                                   data-testid={`role-select-${u.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {u.status === "pending" && (
                    <>
                      <Button onClick={() => approve(u)} size="sm"
                              className="h-8 rounded-sm bg-emerald-600 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-emerald-700"
                              data-testid={`approve-${u.id}`}>
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Aprobar
                      </Button>
                      <Button onClick={() => reject(u)} size="sm" variant="outline"
                              className="h-8 rounded-sm border-red-300 text-[11px] uppercase tracking-[0.16em] text-red-700 hover:bg-red-50"
                              data-testid={`reject-${u.id}`}>
                        <XCircle className="mr-1 h-3.5 w-3.5" /> Rechazar
                      </Button>
                    </>
                  )}
                  {u.status === "rejected" && (
                    <Button onClick={() => approve(u)} size="sm"
                            className="h-8 rounded-sm bg-emerald-600 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-emerald-700"
                            data-testid={`reactivate-${u.id}`}>
                      <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Reactivar
                    </Button>
                  )}
                  <button onClick={() => del(u)}
                          className="text-[11px] text-red-600 hover:underline"
                          data-testid={`delete-${u.id}`}>
                    <Trash2 className="mr-0.5 inline h-3 w-3" /> eliminar
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
